import { isSuccess } from '@yimoka/shared';

import { tenantHttpPost } from '@/http';
import type { AdminRelationBadgeCounts } from '@/mf/types';

interface AdminRelationApplicationCountParams {
  initiatorID: string;
  status: 'pending';
}

interface AdminRelationApprovalCountParams {
  approvalTenantID: string;
  status: 'pending';
}

const inFlightLoads = new Map<string, Promise<AdminRelationBadgeCounts | undefined>>();
const trailingRefreshes = new Map<string, Promise<AdminRelationBadgeCounts | undefined>>();

/** adminRelationCountRequests 查询当前租户仍处于审批中的我方申请数量。 */
export const adminRelationCountRequests = (params: AdminRelationApplicationCountParams) => tenantHttpPost<
  string,
  AdminRelationApplicationCountParams
>('/base/iam/portal/coopRelationRequest/count', params);

/** adminRelationCountApprovals 查询当前租户待处理的审批节点数量。 */
export const adminRelationCountApprovals = (params: AdminRelationApprovalCountParams) => tenantHttpPost<
  string,
  AdminRelationApprovalCountParams
>('/base/iam/portal/coopRelationApproval/count', params);

/** sumRelationBadgeCounts 使用十进制字符串相加，避免 int64 数量转 number 后丢失精度。 */
function sumRelationBadgeCounts(left: string, right: string): string {
  try {
    return String(BigInt(left) + BigInt(right));
  } catch {
    return '0';
  }
}

/** requestAdminRelationBadgeCounts 并行读取两个 Count，任一失败都不发布不完整角标。 */
async function requestAdminRelationBadgeCounts(tenantID: string): Promise<AdminRelationBadgeCounts | undefined> {
  const [applicationResponse, approvalResponse] = await Promise.all([
    adminRelationCountRequests({ initiatorID: tenantID, status: 'pending' }),
    adminRelationCountApprovals({ approvalTenantID: tenantID, status: 'pending' }),
  ]);
  if (!isSuccess(applicationResponse) || !isSuccess(approvalResponse)) return undefined;
  const applications = String(applicationResponse.data ?? '0');
  const approvals = String(approvalResponse.data ?? '0');
  return { applications, approvals, total: sumRelationBadgeCounts(applications, approvals) };
}

/** loadAdminRelationBadgeCounts 按租户合并并发刷新，避免 Host 初始化和页面操作重复请求。 */
export function loadAdminRelationBadgeCounts(tenantID: string): Promise<AdminRelationBadgeCounts | undefined> {
  const current = inFlightLoads.get(tenantID);
  if (current) return current;
  return startAdminRelationBadgeRequest(tenantID);
}

/** startAdminRelationBadgeRequest 发起一次新 Count 请求，并仅清理自己登记的并发快照。 */
function startAdminRelationBadgeRequest(tenantID: string): Promise<AdminRelationBadgeCounts | undefined> {
  const request = requestAdminRelationBadgeCounts(tenantID)
    .catch(() => undefined)
    .finally(() => {
      if (inFlightLoads.get(tenantID) === request) inFlightLoads.delete(tenantID);
    });
  inFlightLoads.set(tenantID, request);
  return request;
}

/** refreshAdminRelationBadgeCounts 等待操作前请求结束后强制读取一次新数量，并合并连续刷新。 */
export function refreshAdminRelationBadgeCounts(tenantID: string): Promise<AdminRelationBadgeCounts | undefined> {
  const trailing = trailingRefreshes.get(tenantID);
  if (trailing) return trailing;
  const previous = inFlightLoads.get(tenantID);
  const refresh = Promise.resolve(previous)
    .catch(() => undefined)
    .then(() => startAdminRelationBadgeRequest(tenantID))
    .finally(() => trailingRefreshes.delete(tenantID));
  trailingRefreshes.set(tenantID, refresh);
  return refresh;
}
